import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { Target, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGES = [
  { value: 'prospect', label: 'Prospect' },
  { value: 'qualified', label: 'Qualified' },
  { value: 'demo', label: 'Demo' },
  { value: 'proposal', label: 'Proposal' },
  { value: 'won', label: 'Won' },
  { value: 'lost', label: 'Lost' },
];

/**
 * One-click "Create Deal" - opens a small light-themed dialog pre-filled from a
 * lead row or a sent email, and POSTs to /api/deals.
 *
 * Props:
 *  - prefill: { company_name, contact_name, contact_email, contact_phone, lead_id }
 *  - source:  string written to deal.source (e.g. 'pipeline', 'sent_email')
 *  - trigger: optional custom trigger node; defaults to a ghost icon button
 *  - onCreated: optional callback(deal)
 */
export const CreateDealButton = ({ prefill = {}, source = 'manual', trigger, triggerClassName, testId = 'create-deal', onCreated }) => {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);

  const buildForm = () => ({
    title: prefill.company_name
      ? `${prefill.company_name} - Deal`
      : prefill.contact_name ? `${prefill.contact_name} - Deal` : 'New Deal',
    company_name: prefill.company_name || '',
    contact_name: prefill.contact_name || '',
    contact_email: prefill.contact_email || '',
    contact_phone: prefill.contact_phone || '',
    value_gbp: '',
    stage: 'prospect',
  });

  const handleOpenChange = (o) => {
    if (o) setForm(buildForm());
    setOpen(o);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error('Deal title is required'); return; }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/deals`, {
        title: form.title.trim(),
        company_name: form.company_name.trim() || null,
        contact_name: form.contact_name.trim() || null,
        contact_email: form.contact_email.trim() || null,
        contact_phone: form.contact_phone.trim() || null,
        value_gbp: form.value_gbp ? parseFloat(form.value_gbp) : null,
        stage: form.stage,
        lead_id: prefill.lead_id || null,
        source,
      });
      toast.success('Deal created in your pipeline');
      setOpen(false);
      onCreated?.(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create deal');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <div
        onClick={(e) => { e.stopPropagation(); handleOpenChange(true); }}
        className="inline-flex"
        data-testid={`${testId}-trigger`}
      >
        {trigger || (
          <Button
            variant="ghost"
            size="sm"
            className={triggerClassName || 'text-slate-400 hover:text-emerald-400 hover:bg-emerald-500/10'}
            title="Create a deal from this record"
            type="button"
          >
            <Target className="w-4 h-4" />
          </Button>
        )}
      </div>
      <DialogContent className="bg-white max-w-lg" data-testid={`${testId}-dialog`} onClick={(e) => e.stopPropagation()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-gray-900">
            <Target className="w-5 h-5 text-emerald-600" /> Create Deal
          </DialogTitle>
          <DialogDescription>Add this to your sales pipeline. Pre-filled from the selected record.</DialogDescription>
        </DialogHeader>
        {form && (
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-2">
              <Label>Deal title</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Acme Ltd - Website redesign"
                data-testid={`${testId}-title`}
                required
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Company</Label>
                <Input value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} data-testid={`${testId}-company`} />
              </div>
              <div className="space-y-2">
                <Label>Contact</Label>
                <Input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} data-testid={`${testId}-contact`} />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Contact email</Label>
                <Input type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} data-testid={`${testId}-email`} />
              </div>
              <div className="space-y-2">
                <Label>Value (£)</Label>
                <Input type="number" min="0" step="any" value={form.value_gbp} onChange={(e) => setForm({ ...form, value_gbp: e.target.value })} placeholder="e.g. 5000" data-testid={`${testId}-value`} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Stage</Label>
              <Select value={form.stage} onValueChange={(v) => setForm({ ...form, stage: v })}>
                <SelectTrigger data-testid={`${testId}-stage`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STAGES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`${testId}-submit`}>
                {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating…</> : <><Target className="w-4 h-4 mr-2" /> Create Deal</>}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CreateDealButton;
